import React, { useContext, useMemo } from 'react'
import { ThemeContext } from 'styled-components'
import JSBI from 'jsbi'
import FullPositionCard from '../../components/PositionCard'
import { useTokenBalancesWithLoadingIndicator } from '../../state/wallet/hooks'
import { TYPE, StyledInternalLink } from '../../theme'
import { Text } from 'rebass'
import { ButtonPrimary } from '../../components/Button'
import { AutoColumn } from '../../components/Column'

import { useActiveWeb3React } from '../../hooks'
import { useV2Pairs } from '../../hooks/useV2Pairs'
import { toV2LiquidityToken, useTrackedTokenPairs } from '../../state/user/hooks'
import { useStakingInfo } from '../../state/stake/hooks'
import { BIG_INT_ZERO } from '../../constants'
import { Pair } from '@uniswap/v2-sdk'
import { LoadingContainer, LoadingRows, MainContentWrapper, NoLiquidity, V2GridContainer } from './styleds'
import { Inbox } from 'react-feather'
import { usePrivy } from '@privy-io/react-auth'

export default function Pool() {
  const theme = useContext(ThemeContext)
  const { login } = usePrivy()
  const { account } = useActiveWeb3React()

  // fetch the user's balances of all tracked V2 LP tokens
  const trackedTokenPairs = useTrackedTokenPairs()
  const tokenPairsWithLiquidityTokens = useMemo(
    () => trackedTokenPairs.map((tokens) => ({ liquidityToken: toV2LiquidityToken(tokens), tokens })),
    [trackedTokenPairs]
  )
  const liquidityTokens = useMemo(
    () => tokenPairsWithLiquidityTokens.map((tpwlt) => tpwlt.liquidityToken),
    [tokenPairsWithLiquidityTokens]
  )
  const [v2PairsBalances, fetchingV2PairBalances] = useTokenBalancesWithLoadingIndicator(
    account ?? undefined,
    liquidityTokens
  )

  // fetch the reserves for all V2 pools in which the user has a balance
  const liquidityTokensWithBalances = useMemo(
    () =>
      tokenPairsWithLiquidityTokens.filter(({ liquidityToken }) =>
        v2PairsBalances[liquidityToken.address]?.greaterThan('0')
      ),
    [tokenPairsWithLiquidityTokens, v2PairsBalances]
  )

  const v2Pairs = useV2Pairs(liquidityTokensWithBalances.map(({ tokens }) => tokens))
  const v2IsLoading =
    fetchingV2PairBalances || v2Pairs?.length < liquidityTokensWithBalances.length || v2Pairs?.some((V2Pair) => !V2Pair)

  const allV2PairsWithLiquidity = v2Pairs.map(([, pair]) => pair).filter((v2Pair): v2Pair is Pair => Boolean(v2Pair))

  // show liquidity even if its deposited in rewards contract
  const stakingInfo = useStakingInfo()
  const stakingInfosWithBalance = stakingInfo?.filter((pool) => JSBI.greaterThan(pool.stakedAmount.raw, BIG_INT_ZERO))
  const stakingPairs = useV2Pairs(stakingInfosWithBalance?.map((stakingInfo) => stakingInfo.tokens))

  // remove any pairs that also are included in pairs with stake in mining pool
  const v2PairsWithoutStakedAmount = allV2PairsWithLiquidity.filter((v2Pair) => {
    return (
      stakingPairs
        ?.map((stakingPair) => stakingPair[1])
        .filter((stakingPair) => stakingPair?.liquidityToken.address === v2Pair.liquidityToken.address).length === 0
    )
  })

  return (
    <>
      <MainContentWrapper>
        {v2IsLoading ? (
          <LoadingContainer>
            <LoadingRows>
              <div />
              <div />
              <div />
              <div />
              <div />
              <div />
              <div />
              <div />
              <div />
              <div />
              <div />
              <div />
            </LoadingRows>
          </LoadingContainer>
        ) : allV2PairsWithLiquidity?.length > 0 || stakingPairs?.length > 0 ? (
          <>
            <V2GridContainer>
              {v2PairsWithoutStakedAmount.map((v2Pair) => (
                <FullPositionCard key={v2Pair.liquidityToken.address} pair={v2Pair} />
              ))}
              {stakingPairs.map(
                (stakingPair, i) =>
                  stakingPair[1] && ( // skip pairs that arent loaded
                    <FullPositionCard
                      key={stakingInfosWithBalance[i].stakingRewardAddress}
                      pair={stakingPair[1]}
                      stakedBalance={stakingInfosWithBalance[i].stakedAmount}
                    />
                  )
              )}
            </V2GridContainer>
            <AutoColumn justify={'center'} gap="md">
              <Text textAlign="center" fontSize={14} style={{ padding: '.5rem 0 .5rem 0', marginTop: '14px' }}>
                {'Don’t see one of your v2 positions?'}{' '}
                <StyledInternalLink id="import-pool-link" to={'/find'}>
                  {'Import it.'}
                </StyledInternalLink>
              </Text>
            </AutoColumn>
          </>
        ) : (
          <LoadingContainer>
            <NoLiquidity>
              <TYPE.mediumHeader color={theme.text3} textAlign="center">
                <Inbox size={48} strokeWidth={1} style={{ marginBottom: '.5rem' }} />
                <div>Your V2 liquidity positions will appear here.</div>
                <AutoColumn justify={'center'} gap="md">
                  <Text textAlign="center" fontSize={14} style={{ marginTop: '14px' }}>
                    {'Don’t see one of your v2 positions?'}{' '}
                    <StyledInternalLink id="import-pool-link" to={'/find'}>
                      {'Import it.'}
                    </StyledInternalLink>
                  </Text>
                </AutoColumn>
              </TYPE.mediumHeader>
              {!account && (
                <ButtonPrimary
                  style={{ marginTop: '1em', padding: '8px 16px' }}
                  onClick={() =>
                    login({
                      loginMethods: ['wallet'],
                      walletChainType: 'ethereum-only',
                      disableSignup: false,
                    })
                  }
                >
                  Connect a wallet
                </ButtonPrimary>
              )}
            </NoLiquidity>
          </LoadingContainer>
        )}
      </MainContentWrapper>
    </>
  )
}
